import __cloneDeep from 'lodash/cloneDeep';
import __get from 'lodash/get';
import _ from 'lodash';

const formatTime = time => time.toString().padStart(2, 0);

const isOffer = (getters, rootGetters, _path) => {
  const rootOffers = rootGetters['offers/getContent'];

  if (!rootOffers || !rootOffers.length) {
    return false;
  }

  const offers = rootOffers.filter(offer => offer.subject.target === _path && rootGetters['offers/isOfferValid'](offer));

  return !!offers.length;
};

export const testConditionalPrice = (state, getters) => (conditionalPrices) => {
  let activePrice;

  conditionalPrices.some((price) => {
    const condition = price.condition;

    if (Array.isArray(condition)) {
      let conditionsLeft = condition.length;

      condition.forEach((path) => {
        if (Array.isArray(path)) {
          const hasOnePathActive = !!path.find(item => getters.isPathActive(item));

          if (hasOnePathActive) {
            conditionsLeft -= 1;
          }
        } else if (getters.isPathActive(path)) {
          conditionsLeft -= 1;
        }
      });

      if (conditionsLeft === 0) {
        activePrice = price;

        return true;
      }
    } else if (getters.isPathActive(condition)) {
      activePrice = price;
    }

    return false;
  });

  return activePrice;
};

export const determineActivePrice = (state, getters) => (priceObj) => {
  let activePrice;

  if (Array.isArray(priceObj)) {
    const conditionalPrices = priceObj.filter(price => price.type === 'conditional');

    if (conditionalPrices.length && getters.testConditionalPrice(conditionalPrices)) {
      activePrice = getters.testConditionalPrice(conditionalPrices);
    } else {
      [activePrice] = priceObj.filter(price => price.type === 'default');
    }
  } else {
    activePrice = priceObj;
  }

  return activePrice;
};

export const determineNonFidelizedPrice = () => (priceObj) => {
  let activePrice;

  if (Array.isArray(priceObj)) {
    const [nonFidelizedConditionalPrice] = priceObj.filter(price => price.condition === 'permanencia.components.months_1');

    if (nonFidelizedConditionalPrice) {
      activePrice = nonFidelizedConditionalPrice;
    } else {
      [activePrice] = priceObj.filter(price => price.type === 'default');
    }
  } else {
    activePrice = priceObj;
  }

  return activePrice;
};

export const getContent = state => state.content;

export const getActiveItems = state => state.content.filter(c => c.quantity > 0);

export const getContentServices = (state) => {
  if (!state.content) {
    return false;
  }

  const filteredContent = state.content.filter(item => item.quantity && item.meta.service);
  return filteredContent.map(item => ({
    serviceCode: item.meta.service.code,
    serviceCodeDescription: item.meta.service.description
  }));
};

export const getStringifiedContent = (state, getters, rootState) => {
  if (!state.content) {
    return false;
  }

  const sid = rootState.simulation.id;

  const date = new Date();
  const time = `${formatTime(date.getHours())}:${formatTime(date.getMinutes())}:${formatTime(date.getSeconds())}`;

  const content = state.content.map((item) => {
    if (!item.quantity) {
      return '';
    }

    const quantity = item.quantity > 1 ? `${item.quantity}x ` : '';
    const name = item.meta.title.track ? item.meta.title.track : item.meta.title.item;

    return `${quantity}${name}`;
  });

  return `${sid};${time};${content.join(';')}`;
};

export const getStringifiedContentAll = (state, getters, rootState) => {
  if (!state.content) {
    return false;
  }

  const sid = rootState.simulation.id;

  const date = new Date();
  const time = `${formatTime(date.getHours())}:${formatTime(date.getMinutes())}:${formatTime(date.getSeconds())}`;

  const content = state.content.map((item) => {
    const quantity = item.quantity > 1 ? `${item.quantity}x ` : '';
    const name = item.meta.title.track ? item.meta.title.track : item.meta.title.item;

    return `${quantity}${name}`;
  });

  return `${sid};${time};${content.join(';')}`;
};

export const getTrackingContent = (state, getters, rootState, rootGetters) => {
  if (!state.content) {
    return null;
  }

  const sid = rootGetters['simulation/getId'];
  const date = new Date();
  const time = `${formatTime(date.getHours())}:${formatTime(date.getMinutes())}:${formatTime(date.getSeconds())}`;

  const packageContents = rootGetters['simulation/getActivePackage'];
  const packageModules = Object.keys(packageContents);
  const packageConfig = rootGetters['packages/getActivePackageConfig'];
  const packageMeta = rootGetters['packages/getActivePackageMeta'];

  const trackingContent = [];

  if (packageConfig.features.enableAnalyticsLeadWithPackage) {
    trackingContent.push(packageMeta.title.track);
  }

  packageModules.forEach((namespace) => {
    const activeItems = getters.activeItemsInNamespace(namespace);
    const content = [];

    activeItems.forEach((item) => {
      const quantity = item.quantity > 1 ? `${item.quantity}x ` : '';
      const name = item.meta.title.track ? item.meta.title.track : item.meta.title.item;

      content.push(`${quantity}${name}`);
    });

    trackingContent.push(content.join(','));

    if (namespace === 'tv') {
      const [appNowo] = state.content.filter(item => item.config.name === 'app_nowo' && item.quantity > 0);

      if (appNowo) {
        const name = appNowo.meta.title.track ? appNowo.meta.title.track : appNowo.meta.title.item;

        trackingContent.push(name);
      } else {
        trackingContent.push('');
      }

      // Box
      if (getters.isPathActive('tv.children.premium.components.not_categorized.components.box')) {
        const [box] = state.content.filter(item => item.path === 'tv.children.premium.components.not_categorized.components.box');
        const name = box.meta.title.track ? box.meta.title.track : box.meta.title.item;

        trackingContent.push(name);
      } else {
        trackingContent.push('');
      }

      // Premium channels
      const activeChannels = state.content.filter(item => item.config.parent === 'tv.children.premium' && item.config.namespace !== 'not_categorized' && item.quantity);
      const channelsContent = [];

      activeChannels.forEach((item) => {
        const name = item.meta.title.track ? item.meta.title.track : item.meta.title.item;

        channelsContent.push(name);
      });

      trackingContent.push(channelsContent.join(','));
    }

    if (namespace === 'telefone') {
      // Rede fixa
      if (getters.isPathActive('telefone.components.voice_line.components.rf_10000')) {
        const [rf] = state.content.filter(item => item.path === 'telefone.components.voice_line.components.rf_10000');
        const name = rf.meta.title.track ? rf.meta.title.track : rf.meta.title.item;

        trackingContent.push(name);
      } else {
        trackingContent.push('');
      }

      // Rede movel
      if (getters.isPathActive('telefone.components.voice_line.components.rm_300')) {
        const [rm] = state.content.filter(item => item.path === 'telefone.components.voice_line.components.rm_300');
        const name = rm.meta.title.track ? rm.meta.title.track : rm.meta.title.item;

        trackingContent.push(name);
      } else {
        trackingContent.push('');
      }

      // Portabilidade
      if (getters.isPathActive('telefone.extra.portabilidade')) {
        const [portability] = state.content.filter(item => item.path === 'telefone.extra.portabilidade');
        const name = portability.meta.title.track ? portability.meta.title.track : portability.meta.title.item;

        trackingContent.push(name);
      } else {
        trackingContent.push('');
      }
    }

    // if (namespace === 'plano') {
    //   // Portabilidade
    //   if (getters.isPathActive('plano.extra.portabilidade')) {
    //     const [portability] = state.content.filter(item => item.path === 'plano.extra.portabilidade');
    //     const name = portability.meta.title.track ? portability.meta.title.track : portability.meta.title.item;

    //     trackingContent.push(name);
    //   } else {
    //     trackingContent.push('');
    //   }
    // }
  });

  return `${sid};${time};${trackingContent.join(';')}`;
};

export const getParsedContent = (state, getters, rootState, rootGetters) => {
  if (!state.content) {
    return false;
  }

  const activeItems = state.content.filter(item => item.quantity >= 1);

  const parsedCart = {
    content: [],
    totals: {
      cart: {
        key: 'Total',
        strikedValue: null,
        value: 0
      },
      installation: {
        key: 'Instalação',
        value: 0
      },
      payment: {
        key: 'Débito Direto',
        value: ''
      },
      portability: {
        key: 'Portabilidade',
        value: 0
      },
      fidelization: {
        key: 'Fidelização',
        value: ''
      },
      invoice: {
        key: 'Fatura Eletrónica',
        value: ''
      }
    },
    offers: null
  };

  activeItems.forEach((item) => {
    if (!item.config.isListable) {
      return false;
    }

    const activePrice = getters.determineActivePrice(item.price);

    let parsedPrice;

    if ((!isOffer(getters, rootGetters, item.path) && (activePrice && !activePrice.included)) || !item.config.isResumable) {
      const basePrice = typeof item.config.basePrice === 'number' ? item.config.basePrice : 0;
      let finalPrice = 0;
      if (!(item.config.namespace === 'fatura_detalhe')) {
        // const shouldUseNewPrice = !!getters.getPath('fatura.components.digital_debitodireto').quantity;
        // if (!shouldUseNewPrice && activePrice.old) {
        //   finalPrice = (basePrice + activePrice.old) * item.quantity;
        // } else {
        //   finalPrice = (basePrice + activePrice.value) * item.quantity;
        // }
        finalPrice = activePrice.old ? (basePrice + activePrice.old) * item.quantity : (basePrice + activePrice.value) * item.quantity;
        const style = finalPrice === 0 ? ' style="color: #B2B2B2"' : '';

        parsedCart.totals.cart.value += finalPrice;

        parsedPrice = `<strong${style}>€${finalPrice.toFixed(2).replace('.', ',')}</strong>`;
        parsedPrice = activePrice.recurring ? `${parsedPrice} /mês` : parsedPrice;
      }
    } else if (!isOffer(getters, rootGetters, item.path)) {
      parsedPrice = '<span style="font-size: 11px; color: #B2B2B2">INCLUÍDO</span>';
    } else {
      parsedPrice = '<span style="font-size: 11px; color: #907595">OFERTA</span>';
    }

    const activeItemQuantity = item.quantity > 1 ? `x${item.quantity} ` : '';

    const noPricePaths = [
      'fatura.components.digital',
      'fatura.components.digital_debitodireto',
      'fatura.components.analog',
      'fatura.components.analog_debitodireto'
    ];

    const excludedPaths = [
      'fatura.components.digital',
      'fatura.components.digital_debitodireto',
      'fatura.components.analog',
      'fatura.components.analog_debitodireto'
    ];

    if (!excludedPaths.includes(item.path)) {
      const activeItem = {
        title: `<strong>${activeItemQuantity}${item.meta.title.cart}</strong>`,
        price: noPricePaths.includes(item.path) ? '' : parsedPrice
      };

      if (item.meta.content) {
        let contents = '';

        item.meta.content.forEach((contentItem) => {
          contents += `<li>${contentItem}</li>`;
        });

        activeItem.content = `<ul style="list-style: none; margin: 0; padding: 0; color: #777;">${contents}</ul>`;
      }

      parsedCart.content.push(activeItem);
    }

    return true;
  });

  let total = parsedCart.totals.cart.value;

  parsedCart.totals.cart.value = `€${total.toFixed(2).replace('.', ',')}`;

  if (getters.namespaceExists('permanencia')) {
    const permanenciaItem = getters.activeItemsInNamespace('permanencia')[0];
    parsedCart.totals.fidelization.value = permanenciaItem.meta.title.cart;
  }

  if (getters.namespaceExists('fatura')) {
    const faturaItem = getters.activeItemsInNamespace('fatura')[0];
    parsedCart.totals.invoice.key = faturaItem.meta.title.cart;
    if (!faturaItem.path.includes('debitodireto')) {
      if (faturaItem.path !== 'fatura.components.analog' && (getters.activeItemsInNamespace('permanencia')[0].path === 'permanencia.components.months_1')) {
        parsedCart.totals.cart.value = `€${(total - 2).toFixed(2).replace('.', ',')}`;
      }
      delete parsedCart.totals.payment;
    }

    if (faturaItem.path.includes('debitodireto')) {
      if (faturaItem.path === 'fatura.components.digital_debitodireto' && getters.activeItemsInNamespace('permanencia')[0].path === 'permanencia.components.months_1') {
        parsedCart.totals.cart.value = `€${(total - 2).toFixed(2).replace('.', ',')}`;
      }
      parsedCart.totals.payment.value = 'ATIVO';
    }
  }

  const simulationPackage = rootGetters['simulation/getPackage'];

  let installation = getters.activeItemsInNamespacePrice('permanencia');
  const freeString = simulationPackage.startsWith('movel') ? 'GRATUITO' : 'OFERTA';
  if (simulationPackage.startsWith('movel')) parsedCart.totals.installation.key = 'Cartão SIM';
  installation = installation === 0 ? freeString : `€${installation.toFixed(2).replace('.', ',')}`;

  if (simulationPackage.startsWith('movel.carregamentos')) {
    installation = getters.isPathActive('numero.components.new') ?
      `€${getters.getPath('numero.components.new').price.value.toFixed(2).replace('.', ',')}` :
      `€${getters.getPath('numero.components.keep').price.value.toFixed(2).replace('.', ',')}`;
  }

  parsedCart.totals.installation.value = installation;

  if (getters.isPathActive('numero.extra.portabilidade')) {
    let portability = getters.getPathPrice('numero.extra.portabilidade').value;
    portability = portability === 0 ? 'GRATUITA' : `€${portability.toFixed(2).replace('.', ',')}`;

    parsedCart.totals.portability.value = portability;
  } else {
    delete parsedCart.totals.portability;
  }

  const activeOffers = [];
  const offers = rootGetters['offers/getContent'].filter(offer => offer.subject.type !== 'discount' && rootGetters['offers/isOfferValid'](offer) && !offer.subject.silentOnStrikedPrice);

  if (offers.length) {
    const offersContents = [];
    offers.forEach((offer) => {
      if (!offer.subject.silentOnStrikedPrice) {
        offersContents.push(`<p style="margin: 0; padding: 0; color: #712C7D; text-transform: uppercase">${offer.emailDescription ? offer.emailDescription : offer.description}</p>`);
      }
    });

    activeOffers.push(...offersContents);
  }

  // check for discount offers
  const discountOffers = rootGetters['offers/getContent'].filter(offer => offer.subject.type === 'discount' && rootGetters['offers/isOfferValid'](offer));

  if (discountOffers.length) {
    const validDiscountOffers = [];

    const activeDiscountOffers = discountOffers.filter((offer) => {
      if (offer.subject.family) {
        if (validDiscountOffers.includes(offer.subject.family)) {
          return false;
        }

        validDiscountOffers.push(offer.subject.family);
      }

      return true;
    });

    const discountOffersContents = [];
    activeDiscountOffers.forEach((offer) => {
      if (!offer.subject.silentOnStrikedPrice) {
        discountOffersContents.push(`<p style="margin: 0; padding: 0; color: #712C7D; text-transform: uppercase">${offer.description}</p>`);
      }
    });

    activeOffers.push(...discountOffersContents);

    // need to remove after end of campaign
    const mappedDiscountOffers = activeDiscountOffers.map(offer => offer.subject.value && !offer.description.includes('15GB') && !offer.description.includes('1GB'));

    let totalDiscount = mappedDiscountOffers.reduce((sum, x) => sum + x);

    const campaignValue = activeDiscountOffers.filter(offer => offer.description.includes('15GB') || offer.description.includes('1GB'));

    if (campaignValue.length) {
      const campaignItems = activeItems.filter(item => item.path.includes('gb_15') || item.path.includes('gb_1'));

      let disc = 0;
      campaignItems.forEach((item) => {
        disc += (item.price[0].old - item.price[0].value) * item.quantity;
      });

      totalDiscount += disc;
    }

    const faturaDiscountFiltered = activeDiscountOffers.filter(el => el.subject.family === 'discount_fatura');

    if (faturaDiscountFiltered.length) {
      totalDiscount -= faturaDiscountFiltered[0].subject.value;
    }


    if (activeItems.filter(i => i.path.includes('fatura.components.analog_debitodireto')).length) {
      total += 2;
    }

    if (activeItems.filter(i => i.path.includes('fatura.components.analog')).length) {
      parsedCart.totals.cart.value = `€${(total + 2).toFixed(2).replace('.', ',')}`;
    }

    if (totalDiscount) {
      const totalWithDiscount = total - totalDiscount;

      if (rootGetters['packages/getActivePackageMeta'] === 'casa') {
        parsedCart.totals.cart.strikedValue = parsedCart.totals.cart.value;
      }

      if (rootGetters['packages/getActivePackageMeta'].name === 'pospago' && (rootGetters['cart/getContent'].filter(item => item.path.includes('gb_15'))[0].quantity >= 1 || rootGetters['cart/getContent'].filter(item => item.path.includes('gb_1'))[0].quantity >= 1) && campaignValue.length) {
        parsedCart.totals.cart.strikedValue = `€${(total - 2).toFixed(2).replace('.', ',')}`;
      }

      parsedCart.totals.cart.value = `€${totalWithDiscount.toFixed(2).replace('.', ',')}`;
    }
  }

  if (rootGetters['packages/getActivePackageMeta'].name === 'carregamentos') {
    delete parsedCart.totals.payment;
    delete parsedCart.totals.fidelization;
    delete parsedCart.totals.invoice;
  }

  if (rootGetters['packages/getActivePackageMeta'].name === 'iptv') {
    delete parsedCart.totals.fidelization;
  }

  parsedCart.offers = activeOffers.join('');

  return parsedCart;
};

export const getItemQuantity = state => (path) => {
  const [content] = state.content.filter(item => item.path === path);

  if (content && content.quantity) {
    return content.quantity;
  }

  return 0;
};

export const getPath = state => (path) => {
  const [content] = state.content.filter(item => item.path === path);

  if (!content) {
    return null;
  }

  return content;
};

export const getPathPrice = (state, getters) => (path) => {
  const [content] = state.content.filter(item => item.path === path);

  if (!content) {
    return null;
  }

  return getters.determineActivePrice(content.price);
};

export const getPathNonFidelizedPrice = (state, getters) => (path) => {
  const [content] = state.content.filter(item => item.path === path);

  if (!content) {
    return null;
  }

  return getters.determineNonFidelizedPrice(content.price);
};

export const isPathInCart = state => path => __cloneDeep(__get(state.content, path));

export const isPathActive = state => (path) => {
  const [content] = state.content.filter(item => item.path === path);

  return content && content.quantity > 0;
};

export const areAllPathsActive = (state, getters) => (paths) => {
  if (Array.isArray(paths)) {
    let size = paths.length;

    paths.forEach((path) => {
      if (Array.isArray(path)) {
        let isActive = false;
        path.forEach((subPath) => {
          if (getters.isPathActive(subPath)) {
            isActive = true;
          }
        });

        if (isActive) size -= 1;
      } else if (getters.isPathActive(path)) {
        size -= 1;
      }
    });

    return !size;
  }

  return getters.isPathActive(paths);
};

export const isPathBeginningWithActive = state => (path) => {
  const [content] = state.content.filter(item => item.path.startsWith(path) && item.quantity > 0);

  return content && content.quantity > 0;
};

export const isPathDisabled = state => (path) => {
  const [content] = state.content.filter(item => item.path === path);

  if (content) {
    return content.config.disabled;
  }

  return null;
};

export const isPathHidden = state => (path) => {
  const [content] = state.content.filter(item => item.path === path);

  if (content) {
    return content.config.hidden;
  }

  return null;
};

export const isPathSuspended = state => (path) => {
  const [content] = state.content.filter(item => item.path === path);

  if (content) {
    return content.config.suspended;
  }

  return null;
};

export const namespaceExists = state => (namespace) => {
  const activeNamespace = state.content.filter(item => item.config.namespace === namespace);

  return !!activeNamespace.length;
};

export const activeItemsInNamespace = state => (namespace) => {
  if (!state.content) {
    return [];
  }

  return state.content.filter(item => item.config.namespace === namespace && item.quantity);
};

export const activeItemsInNamespacePrice = (state, getters) => (namespace) => {
  const activeItems = state.content.filter(item => item.config.namespace === namespace && item.quantity);

  let total = 0;
  activeItems.forEach((item) => {
    const activePrice = getters.determineActivePrice(item.price);

    total += item.quantity * activePrice.value;
  });

  return total;
};

export const activeItemsInNamespaceListingPrice = (state, getters) => (namespace) => {
  const activeItems = state.content.filter(item => item.config.namespace === namespace && item.quantity);

  let total = 0;
  activeItems.forEach((item) => {
    const activePrice = getters.determineActivePrice(item.price);
    const activeValue = activePrice.listingValue || activePrice.value;

    total += activeValue;
  });

  return total;
};

export const activeComponentItemsInNameSpacePrice = (state, getters) => (namespace) => {
  const searchString = `${namespace}.components`;
  const activeItems = state.content.filter(item => item.config.namespace === namespace && item.path.startsWith(searchString) && item.quantity);

  let total = 0;
  activeItems.forEach((item) => {
    const activePrice = getters.determineActivePrice(item.price);

    total += activePrice.value;
  });

  return total;
};

export const activeItemsLength = (state) => {
  if (state.content) {
    const activeItems = state.content.filter(item => item.quantity > 0);

    return activeItems.length;
  }

  return null;
};

export const getPreviousContent = state => state.previousContent;

export const getBaseServicesPrice = (state, getters) => {
  const baseServices = state.content.filter(c => ((c.quantity > 0) && (!_.isNil(c.meta.fis) && c.meta.fis.type !== 'additional')));
  let total = 0;

  baseServices.forEach((item) => {
    total += getters.getPathPrice(item.path).value * item.quantity;
  });

  return total;
};

export const getMovelServicePrice = (state, getters) => {
  const activeMovelItem = getters.activeItemsInNamespace('movel');

  if (activeMovelItem.length) {
    return getters.getPathPrice(activeMovelItem[0].path).value * activeMovelItem[0].quantity;
  }

  return 0;
};

export const getMovelAdicionaisServicesPrice = (state, getters) => {
  const activeMovelItem = getters.activeItemsInNamespace('movel_adicionais');

  if (activeMovelItem.length) {
    return getters.getPathPrice(activeMovelItem[0].path).value * activeMovelItem[0].quantity;
  }

  return 0;
};

export const getCoverageString = (state, getters) => {
  const activeCoverage = getters.activeItemsInNamespace('_coverage_control');

  if (!activeCoverage.length) return '';

  return activeCoverage[0].meta.title.cart || '';
};

export const getTotalCardsValue = (state, getters) => {
  const activeCards = [
    ...getters.activeItemsInNamespace('movel'),
    ...getters.activeItemsInNamespace('movel_adicionais'),
    ...getters.activeItemsInNamespace('plano')
  ];

  let total = 0;
  activeCards.forEach((item) => {
    total += item.quantity;
  });

  return total;
};

export default {
  activeComponentItemsInNameSpacePrice,
  activeItemsInNamespace,
  activeItemsInNamespaceListingPrice,
  activeItemsInNamespacePrice,
  activeItemsLength,
  areAllPathsActive,
  determineActivePrice,
  determineNonFidelizedPrice,
  getActiveItems,
  getBaseServicesPrice,
  getContent,
  getContentServices,
  getCoverageString,
  getItemQuantity,
  getMovelAdicionaisServicesPrice,
  getMovelServicePrice,
  getParsedContent,
  getPath,
  getPathNonFidelizedPrice,
  getPathPrice,
  getPreviousContent,
  getStringifiedContent,
  getStringifiedContentAll,
  getTotalCardsValue,
  getTrackingContent,
  isPathActive,
  isPathBeginningWithActive,
  isPathDisabled,
  isPathHidden,
  isPathInCart,
  isPathSuspended,
  namespaceExists,
  testConditionalPrice
};



// WEBPACK FOOTER //
// ./src/store/modules/cart/getters.js